import { useFantasyReferralStore } from '@app/store/zustand/FantasyGame/Referrals';

import { apiFactory, apiReturnResults } from '../../ApiFactory';
import { Request } from '../../common/api/requests';
import { type ApiResult, type BaseParams, type PostParams, type Token } from '../../common/structs';
import { links } from '../../links';

import {
    AuthProviders,
    type LoginRequestParams,
    type OAuthLoginRequestParams,
    type ProviderAuthRequestParams,
    type RefreshTokenRequestParams,
    type TwitterLoginRequestParams,
} from './types';

const authHeaders = new Headers({
    'Sec-Fetch-Site': 'same-origin',
});

export async function refreshToken(
    params: BaseParams & PostParams<RefreshTokenRequestParams>,
): Promise<ApiResult<Token>> {
    return Request.postRequest({
        ...params,
        apiSettingsType: 'auth',
        target: links.w3auth().token().refresh(),
    });
}

export async function login(params: BaseParams & PostParams<LoginRequestParams>): Promise<ApiResult<Token>> {
    return Request.postRequest({
        ...params,
        apiSettingsType: 'auth',
        target: links.w3auth().email().login(),
    });
}

export const callLogin = async (params: BaseParams & LoginRequestParams): Promise<Token> => {
    const result = await apiFactory().call(login, {
        ...params,
        value: {
            alternate_id: params.alternate_id,
            captcha_response: params.captcha_response,
            email: params.email,
            password: params.password,
        },
    });
    if (result.ok) {
        return result.value;
    }
    const errorMessage = result?.error?.detail ?? 'Error login';
    throw new Error(errorMessage);
};

function getProviderAuthUrl(provider: AuthProviders) {
    switch (provider) {
        case AuthProviders.Apple:
            return links.w3auth().apple().auth();
        case AuthProviders.Discord:
            return links.w3auth().discord().auth();
        case AuthProviders.Google:
            return links.w3auth().google().auth();
        case AuthProviders.Twitter:
            return links.w3auth().twitter().auth();
    }
}

export async function providerAuth(
    provider: AuthProviders,
    params: BaseParams & PostParams,
): Promise<ApiResult<string>> {
    return Request.postRequest({
        ...params,
        apiSettingsType: 'auth',
        target: getProviderAuthUrl(provider),

        additionalHeaders: authHeaders,
    });
}

export const callProviderAuth = async (provider: AuthProviders): Promise<string> => {
    const result = await apiFactory().call((factoryParams) => providerAuth(provider, factoryParams), { value: {} });

    if (!result.ok) {
        const errorMessage = result?.error?.detail ?? `Error: ${provider} authentication`;
        throw new Error(errorMessage);
    }

    if (provider === AuthProviders.Apple) {
        const modifiedUrl = new URL(result.value);
        modifiedUrl.searchParams.append('response_mode', 'form_post');
        return modifiedUrl.toString();
    } else {
        return result.value;
    }
};

function getProviderSignInUrl(provider: AuthProviders) {
    switch (provider) {
        case AuthProviders.Apple:
            return links.w3auth().apple().login();
        case AuthProviders.Discord:
            return links.w3auth().discord().login();
        case AuthProviders.Google:
            return links.w3auth().google().login();
        case AuthProviders.Twitter:
            return links.w3auth().twitter().login();
    }
}

export const callGenericProviderSignIn = async (
    provider: AuthProviders,
    params: BaseParams & OAuthLoginRequestParams,
): Promise<Token> => {
    const { clearCode, referralCode } = useFantasyReferralStore.getState();
    const result = await apiFactory()
        .call((factoryParams) => genericProviderSignIn(provider, factoryParams), {
            ...params,
            value: { token: params.code, verifier: params.state, referral_code: referralCode },
        })
        .then((res) => {
            clearCode();
            return res;
        });

    if (!result.ok) {
        const errorMessage = result?.error?.detail ?? `Error: ${provider} authentication`;
        throw new Error(errorMessage);
    }

    return result.value;
};

export function genericProviderSignIn(
    provider: AuthProviders,
    params: BaseParams & PostParams<ProviderAuthRequestParams>,
): Promise<ApiResult<Token>> {
    const providerAuthUrl = getProviderSignInUrl(provider);

    const query =
        provider === AuthProviders.Twitter
            ? {
                  oauth_token: params.value.token,
                  oauth_verifier: params.value.verifier,
              }
            : {
                  auth_code: params.value.token,
                  csrf_token: params.value.verifier,
              };

    return Request.postRequest({
        ...params,
        apiSettingsType: 'auth',
        target: providerAuthUrl,
        query,

        additionalHeaders: authHeaders,
    });
}

export const callTokenRefresh = (params: BaseParams & PostParams<RefreshTokenRequestParams>) =>
    apiReturnResults<Token>(refreshToken, params);

export const callTwitterProviderSignIn = async (params: BaseParams & TwitterLoginRequestParams): Promise<Token> => {
    const { clearCode, referralCode } = useFantasyReferralStore.getState();

    const result = await apiFactory()
        .call((factoryParams) => genericProviderSignIn(AuthProviders.Twitter, factoryParams), {
            ...params,
            value: { token: params.oAuthToken, verifier: params.oAuthVerifier, referral_code: referralCode },
        })
        .then((res) => {
            clearCode();
            return res;
        });

    if (!result.ok) {
        const errorMessage = result?.error?.detail ?? `Error: Twitter authentication`;
        throw new Error(errorMessage);
    }

    return result.value;
};
